import type { UseFormSetError } from 'react-hook-form';

import type {
  GetInviteEligibilityParams,
  InviteEligibilityApplicant,
  InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';
import type { Renter } from '@/utils/types/renter.interface';

type SetErrorFn = UseFormSetError<Renter>;

/** Matches DDS `InviteEligibilityBlockErrorCode` on create 409 metadata. */
export const INVITE_ELIGIBILITY_BLOCK_ERROR_CODE = 'invite_eligibility_block';

/** RHF error type so eligibility blocks can be cleared without wiping other errors. */
export const INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE = 'invite_eligibility_block';

/**
 * Non-empty so the text field enters error (red) state; zero-width so we don't
 * duplicate the banner copy under the input.
 */
export const INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE = '\u200b';

export type SubmitErrorFieldError = {
  field: string;
  message: string;
  /** Present when create refused a row as eligibility block (TOD / future DDS shape). */
  outcome?: string;
};

export type SubmitErrorData = {
  message?: string;
  field_errors?: SubmitErrorFieldError[];
  metadata?: {
    error_code?: string;
    eligibility?: InviteEligibilityResponseData;
  };
};

// Maps DD service / invite-service field_errors names to React Hook Form paths
const API_TO_FORM_FIELD: Record<string, string> = {
  gross_monthly_rent: 'grossMonthlyRent',
  lease_start: 'leaseStartDate',
  lease_end: 'leaseEndDate',
  prepaid_rent: 'prepaidRent',
  free_rent: 'freeRent',
  unit: 'unit',
  screening_status: 'screeningStatus',
  'coverage.lease_guarantee.coverage_amount': 'leaseGuaranteeMonthsOverride',
  'coverage.security_deposit_replacement.coverage_amount':
    'securityDepositReplacementAmountOverride',
};

const inviteRecipientEmailIndex = (apiField: string): number | null => {
  const match = /^invite_recipients\.(\d+)\.email$/.exec(apiField);
  if (match) {
    return Number(match[1]);
  }
  if (apiField === 'email') {
    return 0;
  }
  return null;
};

const mapApiFieldToFormField = (apiField: string): string | null => {
  const recipientIndex = inviteRecipientEmailIndex(apiField);
  if (recipientIndex !== null) {
    return `applicantEmails.${recipientIndex}.value`;
  }

  return API_TO_FORM_FIELD[apiField] ?? null;
};

/** Create and edit 409 bodies. Edit fields are optional; create fields are required. */
type InviteEligibilityBlockErrorBody = {
  metadata?: { error_code?: string };
  field_errors?: ReadonlyArray<{
    field?: string;
    message?: string;
    outcome?: string;
  }>;
};

export const isInviteEligibilityBlockError = (
  data: InviteEligibilityBlockErrorBody | undefined,
): boolean => {
  if (!data) {
    return false;
  }
  if (data.metadata?.error_code === INVITE_ELIGIBILITY_BLOCK_ERROR_CODE) {
    return true;
  }
  return Boolean(data.field_errors?.some((error) => error.outcome === 'block'));
};

const synthesizeBlockedApplicant = (index: number): InviteEligibilityApplicant => ({
  index,
  email: '',
  outcome: 'block',
  reason: 'same_address_and_unit',
  total_matches: 1,
  truncated: false,
  matches: [],
});

const synthesizeEligibilityFromFieldErrors = (
  fieldErrors: SubmitErrorFieldError[],
): InviteEligibilityResponseData | null => {
  const applicants: InviteEligibilityApplicant[] = [];
  for (const error of fieldErrors) {
    if (error.outcome !== 'block') {
      continue;
    }
    const index = inviteRecipientEmailIndex(error.field);
    if (index === null) {
      continue;
    }
    applicants.push(synthesizeBlockedApplicant(index));
  }

  if (applicants.length === 0) {
    return null;
  }

  return { outcome: 'block', applicants };
};

/**
 * Prefer `metadata.eligibility` from DDS. If only field_errors carry `outcome: "block"`,
 * synthesize a minimal eligibility payload so banners can still render.
 */
export const getEligibilityFromSubmitError = (
  data: SubmitErrorData | undefined,
): InviteEligibilityResponseData | null => {
  if (!data) {
    return null;
  }
  if (data.metadata?.eligibility) {
    return data.metadata.eligibility;
  }
  if (!isInviteEligibilityBlockError(data) || !data.field_errors?.length) {
    return null;
  }
  return synthesizeEligibilityFromFieldErrors(data.field_errors);
};

const resolvePropertyId = (address: Renter['address']): string =>
  address?.property_alpha_id?.trim() || address?.property_id?.trim() || '';

const resolveAddressId = (address: Renter['address']): string | undefined =>
  address?.address_alpha_id?.trim() || address?.address_id?.trim() || undefined;

/** Build the same eligibility query key inputs the preflight hook uses, from submit values. */
export const buildEligibilityParamsFromRenter = (
  values: Renter,
): GetInviteEligibilityParams | null => {
  const propertyId = resolvePropertyId(values.address);
  if (!propertyId) {
    return null;
  }

  const emails = (values.applicantEmails ?? [])
    .map(({ value }) => value?.trim().toLowerCase() ?? '')
    .filter(Boolean);

  const unit = values.unit?.trim() || '';

  if (emails.length === 0 || !unit) {
    return null;
  }

  return {
    property_id: propertyId,
    emails,
    unit,
    address_id: resolveAddressId(values.address),
  };
};

const isEligibilityBlockFieldError = (
  error: SubmitErrorFieldError,
  treatRecipientEmailsAsBlock: boolean,
): boolean => {
  if (error.outcome === 'block') {
    return true;
  }
  return treatRecipientEmailsAsBlock && inviteRecipientEmailIndex(error.field) !== null;
};

export const applyInviteServiceFieldErrors = (
  fieldErrors: SubmitErrorFieldError[],
  setError: SetErrorFn,
  options?: { treatRecipientEmailsAsEligibilityBlock?: boolean },
): Array<{ formField: string; message: string }> => {
  const treatRecipientEmailsAsBlock = Boolean(options?.treatRecipientEmailsAsEligibilityBlock);
  const applied: Array<{ formField: string; message: string }> = [];

  fieldErrors.forEach((error) => {
    const formField = mapApiFieldToFormField(error.field);
    if (!formField) {
      return;
    }

    if (isEligibilityBlockFieldError(error, treatRecipientEmailsAsBlock)) {
      setError(formField as Parameters<SetErrorFn>[0], {
        type: INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE,
        message: INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE,
      });
      applied.push({ formField, message: error.message });
      return;
    }

    setError(formField as Parameters<SetErrorFn>[0], { message: error.message });
    applied.push({ formField, message: error.message });
  });

  return applied;
};
